// Abuse guards for the Alma chat. Pure logic plus WebCrypto only (no Deno
// APIs), so the edge functions, the browser clients and the vitest suite can
// all import it. See docs/alma/README.md for the threat model and how the
// layers fit together.
import { ALMA_QUESTION_MARKER } from './almaPhase2Config.ts';

// ── Limits (one place to tune; the README documents each) ───────────────────

export const ALMA_LIMITS = {
  /** Most messages a request may carry. */
  maxMessages: 200,
  /** A traveler's message is cut to this many characters (the composers stop typing here too). */
  maxUserMessageChars: 1000,
  /** Alma's own earlier replies are short by rule; this is a generous ceiling. */
  maxAssistantMessageChars: 4000,
  /** Largest request body we will parse. */
  maxBodyBytes: 300_000,
  /** Alma's replies are 60 words by rule, so a reply never needs more than this. Overrides a higher admin setting. */
  maxOutputTokens: 600,
  /** Signed-out /try chat, keyed by a hash of the caller's IP. */
  public: { perWindow: 40, windowSeconds: 15 * 60, perDay: 150, dayTokens: 150_000, globalDayTokens: 4_000_000 },
  /** Signed-in chat, keyed by user id. */
  user: { perWindow: 40, windowSeconds: 15 * 60, perDay: 200, dayTokens: 200_000 },
  /** Building the profile and ranking hosts runs three large-model calls, so it gets its own small daily allowance per user. */
  finalizePerDay: 15,
  /** Declined off-topic requests allowed per day before Alma stops answering until tomorrow. */
  offTopicStrikes: 3,
} as const;

// ── Markers ─────────────────────────────────────────────────────────────────

export const ALMA_COMPLETE_MARKER = '<<<ALMA_COMPLETE>>>';
/** Alma ends a message that declines an unrelated request with this. The server counts it; clients hide it. */
export const ALMA_OFF_TOPIC_MARKER = '<<<ALMA_OFF_TOPIC>>>';

const ALL_MARKERS = [ALMA_COMPLETE_MARKER, ALMA_QUESTION_MARKER, ALMA_OFF_TOPIC_MARKER];

/**
 * What the traveler sees of a reply: markers removed, and a marker still being
 * streamed (a partial "<<<ALMA_CO" at the end) held back. The clients render
 * with this and the server signs its output of this, so both always agree.
 */
export function stripAlmaMarkers(text: string): string {
  for (const marker of [ALMA_QUESTION_MARKER, ALMA_OFF_TOPIC_MARKER]) {
    if (text.includes(marker)) text = text.split(marker).join('').trimEnd();
  }
  if (text.includes(ALMA_COMPLETE_MARKER)) {
    return text.slice(0, text.indexOf(ALMA_COMPLETE_MARKER)).trimEnd();
  }
  // Longest partial first, so "<<<" is held back whole rather than one "<" at a time.
  for (const marker of ALL_MARKERS) {
    for (let i = marker.length - 1; i >= 1; i--) {
      if (text.endsWith(marker.slice(0, i))) return text.slice(0, text.length - i).trimEnd();
    }
  }
  return text;
}

export function isOffTopicReply(raw: string): boolean {
  return raw.includes(ALMA_OFF_TOPIC_MARKER);
}

/** What Alma says, without calling the model, once someone has used up their off-topic strikes today. */
export const OFF_TOPIC_BLOCK_MESSAGE =
  "I'm only here to find you the right host, so I'll pause our chat for today. You're welcome to come back tomorrow.";

// ── Request validation ──────────────────────────────────────────────────────

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  /** Signature the server attached to one of its own replies (see signReply). */
  sig?: string;
}

export type TranscriptResult =
  | { ok: true; turns: ChatTurn[]; truncated: number }
  | { ok: false; error: string };

/**
 * Checks and cleans a transcript sent by a client. Roles are coerced (anything
 * that isn't "assistant" is a traveler turn, never a privileged one), each
 * message is cut to its limit, and the request is refused only when it is
 * structurally wrong or implausibly large.
 */
export function validateTranscript(raw: unknown): TranscriptResult {
  if (!Array.isArray(raw)) return { ok: false, error: 'Invalid messages' };
  if (raw.length > ALMA_LIMITS.maxMessages) return { ok: false, error: 'Invalid messages' };
  const turns: ChatTurn[] = [];
  let truncated = 0;
  for (const item of raw) {
    if (!item || typeof item !== 'object') return { ok: false, error: 'Invalid messages' };
    const { role, content, sig } = item as { role?: unknown; content?: unknown; sig?: unknown };
    if (typeof content !== 'string') return { ok: false, error: 'Invalid messages' };
    const isAssistant = role === 'assistant';
    const limit = isAssistant ? ALMA_LIMITS.maxAssistantMessageChars : ALMA_LIMITS.maxUserMessageChars;
    const text = content.length > limit ? (truncated++, content.slice(0, limit)) : content;
    turns.push({
      role: isAssistant ? 'assistant' : 'user',
      content: text,
      ...(isAssistant && typeof sig === 'string' && /^[0-9a-f]{64}$/.test(sig) ? { sig } : {}),
    });
  }
  return { ok: true, turns, truncated };
}

/** A country hint is a short label; anything else is ignored. */
export function cleanCountry(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const c = raw.trim();
  return c && c.length <= 60 ? c : null;
}

// ── Reply signing: a client can only replay Alma's real replies ──────────────

export type SigningMode = 'off' | 'log' | 'enforce';

export function signingMode(raw: string | undefined | null): SigningMode {
  return raw === 'off' || raw === 'enforce' ? raw : 'log';
}

async function hmacHex(secret: string, data: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(data)));
  return Array.from(mac, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Signature for a reply, over what the traveler sees (markers stripped). */
export async function signReply(secret: string, rawReply: string): Promise<string> {
  return hmacHex(secret, `alma-reply-v1:${stripAlmaMarkers(rawReply)}`);
}

/** A non-reversible, stable label for a caller, so raw IPs are never stored. */
export async function callerKey(secret: string, ip: string): Promise<string> {
  return (await hmacHex(secret, `caller-v1:${ip}`)).slice(0, 16);
}

function sameHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export interface HistoryCheck {
  turns: ChatTurn[];
  /** Assistant turns that were not signed by us. */
  unverified: number;
}

/**
 * Looks at every assistant turn in the history. In "enforce" mode an
 * unverified one is dropped (the model never sees words Alma didn't say); in
 * "log" mode it is kept and only counted, which is the default until every
 * installed app sends signatures back.
 */
export async function checkHistory(secret: string, turns: ChatTurn[], mode: SigningMode): Promise<HistoryCheck> {
  if (mode === 'off') return { turns, unverified: 0 };
  let unverified = 0;
  const kept: ChatTurn[] = [];
  for (const turn of turns) {
    if (turn.role !== 'assistant') { kept.push(turn); continue; }
    const expected = await signReply(secret, turn.content);
    const ok = !!turn.sig && sameHex(turn.sig, expected);
    if (!ok) unverified++;
    if (ok || mode === 'log') kept.push(turn);
  }
  return { turns: kept, unverified };
}

// ── Token accounting from the model's stream ────────────────────────────────

export interface UsageTally { input: number; output: number }

/** Adds what a stream event says it used. Counts input (uncached + cache writes) and output; cache reads are nearly free. */
export function addStreamUsage(tally: UsageTally, event: unknown): void {
  const e = event as {
    type?: string;
    message?: { usage?: { input_tokens?: number; cache_creation_input_tokens?: number; output_tokens?: number } };
    usage?: { output_tokens?: number };
  };
  if (e?.type === 'message_start' && e.message?.usage) {
    const u = e.message.usage;
    tally.input = (u.input_tokens ?? 0) + (u.cache_creation_input_tokens ?? 0);
    tally.output = Math.max(tally.output, u.output_tokens ?? 0);
  } else if (e?.type === 'message_delta' && typeof e.usage?.output_tokens === 'number') {
    tally.output = Math.max(tally.output, e.usage.output_tokens);
  }
}

export const totalTokens = (t: UsageTally) => t.input + t.output;

/** The cap on a reply: the admin setting if lower, never above the ceiling. */
export function replyTokenCap(adminSetting: unknown): number {
  const n = typeof adminSetting === 'number' ? adminSetting : parseInt(String(adminSetting ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? Math.min(n, ALMA_LIMITS.maxOutputTokens) : ALMA_LIMITS.maxOutputTokens;
}

// ── Output hygiene for model-written text that ends up in the database ──────

export const MIN_TRIP_DAYS = 1;
export const MAX_TRIP_DAYS = 30;

/** A trip length is a whole number of days from 1 to 30; anything else is not a real answer. */
export function validTripDays(n: unknown): number | null {
  return typeof n === 'number' && Number.isInteger(n) && n >= MIN_TRIP_DAYS && n <= MAX_TRIP_DAYS ? n : null;
}

/** A model-written line shown to travelers and hosts: no markers, no links, one paragraph, capped. */
export function cleanModelText(text: unknown, maxChars: number): string {
  if (typeof text !== 'string') return '';
  let t = text;
  for (const m of ALL_MARKERS) t = t.split(m).join(' ');
  t = t.replace(/https?:\/\/\S+/gi, '').replace(/\s+/g, ' ').trim();
  return t.length > maxChars ? `${t.slice(0, maxChars - 1).trimEnd()}…` : t;
}

export function cleanReasons(reasons: unknown): string[] {
  if (!Array.isArray(reasons)) return [];
  return reasons
    .map((r) => cleanModelText(r, 60))
    .filter(Boolean)
    .slice(0, 3);
}
